import { FabricObject, StaticCanvas, Textbox } from 'fabric';
import { walkObjects } from '../../canvas/utils/walk-objects';

export interface TextStyleRule {
  /** Substring to find in the textbox's text. Case-sensitive. */
  match: string;
  /** Style delta to apply on each matched range — same shape as Fabric's StyleRange.style. */
  style: Record<string, unknown>;
}

function readRules(obj: FabricObject): TextStyleRule[] | undefined {
  const rules = obj.textStyleRules;
  if (!Array.isArray(rules) || rules.length === 0) return undefined;
  return rules;
}

const lastRuledText = new WeakMap<Textbox, string>();

export function markTextStyleRulesApplied(textbox: Textbox): void {
  lastRuledText.set(textbox, textbox.text ?? '');
}

/** Resets and re-applies per-char styles based on substring matches.
 *  Longest match wins on overlaps — protects specific phrases from being
 *  partially overwritten by shorter rules contained inside them. */
export function applyTextStyleRules(textbox: Textbox): void {
  const rules = readRules(textbox);
  if (!rules) return;

  const text = textbox.text ?? '';
  textbox.set('styles', []);
  lastRuledText.set(textbox, text);
  if (!text) return;

  const byLengthDesc = [...rules].sort((a, b) => b.match.length - a.match.length);
  const claimed = new Set<number>();

  for (const rule of byLengthDesc) {
    if (!rule.match) continue;
    let idx = text.indexOf(rule.match);
    while (idx !== -1) {
      const end = idx + rule.match.length;
      let overlaps = false;
      for (let i = idx; i < end; i++) {
        if (claimed.has(i)) {
          overlaps = true;
          break;
        }
      }
      if (!overlaps) {
        textbox.setSelectionStyles(rule.style, idx, end);
        for (let i = idx; i < end; i++) claimed.add(i);
      }
      idx = text.indexOf(rule.match, end);
    }
  }
}

/** Applies rules only if the text has changed since rules last touched this textbox.
 *  Use this where you want to preserve a user's per-char edits across re-renders. */
export function refreshTextStyleRules(textbox: Textbox): void {
  if (!readRules(textbox)) return;
  if (lastRuledText.get(textbox) === (textbox.text ?? '')) return;
  applyTextStyleRules(textbox);
}

/** Walks the canvas and refreshes rules on every Textbox that declares them
 *  (so a user's bold edits survive the next binding pipeline pass). */
export function applyAllTextStyleRules(canvas: StaticCanvas): void {
  walkObjects(canvas, obj => {
    if (obj instanceof Textbox) refreshTextStyleRules(obj);
  });
}
